import { useState, useCallback, useMemo } from "react";
import type { Enduser } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";
import { useUploadTellescopeFile } from "@tellescope/tanstack-query";
import type { ProfileEditBlock, ProfileFieldKey, ProfileFieldConfig } from "../../shared/schema";

// Default labels for profile fields
const DEFAULT_FIELD_LABELS: Record<ProfileFieldKey, string> = {
  fname: "First Name",
  lname: "Last Name",
  email: "Email",
  phone: "Phone",
  addressLineOne: "Address Line 1",
  addressLineTwo: "Address Line 2",
  city: "City",
  state: "State",
  zipCode: "ZIP Code",
};

// All available profile fields in display order
const ALL_FIELD_KEYS: ProfileFieldKey[] = [
  "fname",
  "lname",
  "email",
  "phone",
  "addressLineOne",
  "addressLineTwo",
  "city",
  "state",
  "zipCode",
];

// Type for the form values (subset of Enduser)
export type ProfileFormValues = Pick<
  Enduser,
  "fname" | "lname" | "email" | "phone" | "addressLineOne" | "addressLineTwo" | "city" | "state" | "zipCode"
>;

// Field configuration with resolved values
export interface ResolvedFieldConfig {
  key: ProfileFieldKey;
  label: string;
  visible: boolean;
  editable: boolean;
  required: boolean;
  value: string;
}

export interface UseProfileEditOptions {
  block: ProfileEditBlock;
}

export interface UseProfileEditResult {
  // Form state
  values: ProfileFormValues;
  initialValues: ProfileFormValues;
  isDirty: boolean;

  // Field configuration
  fields: ResolvedFieldConfig[];

  // Actions
  setValue: (key: ProfileFieldKey, value: string) => void;
  resetForm: () => void;
  submitForm: () => Promise<void>;

  // Status
  isSubmitting: boolean;
  error: string | null;
  successMessage: string | null;

  // Avatar upload — compatible with ImageUploadField's onUpload contract
  onAvatarUpload: (file: File) => Promise<string>;
}

function getInitialValues(enduser: Enduser | null): ProfileFormValues {
  return {
    fname: enduser?.fname ?? "",
    lname: enduser?.lname ?? "",
    email: enduser?.email ?? "",
    phone: enduser?.phone ?? "",
    addressLineOne: enduser?.addressLineOne ?? "",
    addressLineTwo: enduser?.addressLineTwo ?? "",
    city: enduser?.city ?? "",
    state: enduser?.state ?? "",
    zipCode: enduser?.zipCode ?? "",
  };
}

// Fields that should default to not required
const NON_REQUIRED_BY_DEFAULT: ProfileFieldKey[] = ["addressLineTwo"];

function getFieldConfig(
  key: ProfileFieldKey,
  config?: Partial<ProfileFieldConfig>
): Omit<ResolvedFieldConfig, "key" | "value"> {
  // Defaults: visible=true, editable=true, required=true (except addressLineTwo)
  const defaultRequired = !NON_REQUIRED_BY_DEFAULT.includes(key);
  return {
    label: config?.label ?? DEFAULT_FIELD_LABELS[key],
    visible: config?.visible ?? true,
    editable: config?.editable ?? true,
    required: config?.required ?? defaultRequired,
  };
}

export async function uploadAvatar(
  file: File,
  deps: {
    session: { api: { endusers: { updateOne: (id: string, updates: { avatar: string }) => Promise<unknown> } } } | null;
    currentEnduser: { id: string } | null;
    uploadFileAsync: (
      params: { name: string; size: number; type: string; enduserId: string },
      file: File
    ) => Promise<{ secureName: string }>;
    updateCurrentEnduser: (updates: { avatar: string }) => void;
    successMessage: string;
  }
): Promise<{ error: string | null; successMessage: string | null }> {
  const { session, currentEnduser, uploadFileAsync, updateCurrentEnduser, successMessage } = deps;
  if (!session || !currentEnduser) {
    return { error: "Not authenticated", successMessage: null };
  }
  try {
    const fileRecord = await uploadFileAsync(
      { name: file.name, size: file.size, type: file.type, enduserId: currentEnduser.id },
      file
    );
    await session.api.endusers.updateOne(currentEnduser.id, { avatar: fileRecord.secureName });
    updateCurrentEnduser({ avatar: fileRecord.secureName });
    return { error: null, successMessage };
  } catch (err) {
    return { error: err instanceof Error ? err.message : "Failed to upload avatar", successMessage: null };
  }
}

export function useProfileEdit({ block }: UseProfileEditOptions): UseProfileEditResult {
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const updateCurrentEnduser = useEnduserStore((state) => state.updateCurrentEnduser);
  const session = useEnduserStore((state) => state.session);

  const initialValues = useMemo(() => getInitialValues(currentEnduser), [currentEnduser]);
  const [values, setValues] = useState<ProfileFormValues>(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { uploadFileAsync } = useUploadTellescopeFile({
    session,
    allowedFileTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
    maxFileSize: 5 * 1024 * 1024,
    returnDownloadURL: true,
  });

  const onAvatarUpload = useCallback(
    async (file: File): Promise<string> => {
      if (!session || !currentEnduser) throw new Error("Not authenticated");
      const fileRecord = await uploadFileAsync(
        { name: file.name, size: file.size, type: file.type, enduserId: currentEnduser.id },
        file
      );
      await session.api.endusers.updateOne(currentEnduser.id, { avatar: fileRecord.secureName });
      updateCurrentEnduser({ avatar: fileRecord.secureName });
      setSuccessMessage(block.props.successMessage);
      return fileRecord.downloadURL ?? "";
    },
    [session, currentEnduser, uploadFileAsync, updateCurrentEnduser, block.props.successMessage]
  );

  // Build resolved field configurations
  const fields = useMemo<ResolvedFieldConfig[]>(() => {
    return ALL_FIELD_KEYS.map((key) => {
      const config = block.props.fields[key];
      const resolvedConfig = getFieldConfig(key, config);
      return {
        key,
        ...resolvedConfig,
        value: values[key] ?? "",
      };
    }).filter((field) => field.visible);
  }, [block.props.fields, values]);

  // Check if form has changed
  const isDirty = useMemo(() => {
    return ALL_FIELD_KEYS.some((key) => values[key] !== initialValues[key]);
  }, [values, initialValues]);

  const setValue = useCallback((key: ProfileFieldKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear messages when user starts editing
    setError(null);
    setSuccessMessage(null);
  }, []);

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setError(null);
    setSuccessMessage(null);
  }, [initialValues]);

  const submitForm = useCallback(async () => {
    if (!session || !currentEnduser) {
      setError("Not authenticated");
      return;
    }

    // Validate required fields
    for (const field of fields) {
      if (field.required && field.editable && !field.value.trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }

    // Build updates object with only changed editable fields
    const updates: Partial<ProfileFormValues> = {};
    for (const field of fields) {
      if (field.editable && values[field.key] !== initialValues[field.key]) {
        updates[field.key] = values[field.key];
      }
    }

    if (Object.keys(updates).length === 0) {
      setSuccessMessage(block.props.successMessage);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await session.api.endusers.updateOne(currentEnduser.id, updates);
      // Update the store with the new values
      updateCurrentEnduser(updates);
      setSuccessMessage(block.props.successMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  }, [session, currentEnduser, fields, values, initialValues, block.props.successMessage, updateCurrentEnduser]);

  return {
    values,
    initialValues,
    isDirty,
    fields,
    setValue,
    resetForm,
    submitForm,
    isSubmitting,
    error,
    successMessage,
    onAvatarUpload,
  };
}
